/**
 * Contenu du site Centre Sud Le Mans (textes, images, liens).
 *
 * Le contenu vient de Strapi (`GET /api/page/home`, agrégateur), transformé vers
 * la forme de CONTENT ci-dessous. CONTENT sert de secours champ par champ :
 * CMS injoignable, média non uploadé ou champ vide → valeur locale.
 *
 * STRAPI_URL vide → contenu local uniquement, sans appel réseau.
 */

import axios from 'axios'

// Back-office Strapi Cloud : le contenu du site vient de là par défaut.
// Surcharge possible via VUE_APP_STRAPI_URL (dev/.env.local) :
//   - vide                    -> contenu local uniquement, aucun appel réseau
//   - http://localhost:1337   -> Strapi lancé en local
export const STRAPI_URL =
  process.env.VUE_APP_STRAPI_URL ?? 'https://sincere-respect-05f53aa548.strapiapp.com'

const CONTENT = {
  seo: {
    lang: 'fr',
    title: 'Centre Sud Le Mans - Boutiques & restaurants',
    description:
      'Centre Sud Le Mans - centre commercial : boutiques et restaurants, bons plans, actualités, services, accès et horaires. 257 avenue Georges Durand, 72100 Le Mans.',
    keywords: 'Centre Sud, Le Mans, centre commercial, boutiques, restaurants, Carrefour, bons plans, horaires',
    // TODO domaine de production : remplacer ici + dans public/robots.txt et public/sitemap.xml
    siteUrl: 'https://www.centresud-lemans.fr',
    ogImage: '/assets/img/og-image.jpg',
  },

  site: {
    logo: '/assets/pictos/header/logo-centre-sud.svg',
    openLabel: 'Ouvert de 10:00 à 21:30',
  },

  nav: {
    items: [
      { label: 'Boutiques & Restaurants', anchor: '#boutiques' },
      { label: 'Bons plans', anchor: '#bons-plans' },
      { label: 'Actualités', anchor: '#actualites' },
      { label: 'Services', anchor: '#services' },
      { label: 'Accès', anchor: '#acces' },
      { label: 'Plan et horaires', anchor: '#plan-horaires' },
    ],
  },

  // Slider d'accueil : une image par format (desktop paysage / mobile portrait),
  // texte inclus dans le visuel. startsAt/endsAt vides = visible en permanence.
  hero: {
    slides: [
      {
        imageDesktop: '/assets/img/hero/halloween-atelier-desktop.jpg',
        imageMobile: '/assets/img/hero/halloween-atelier-mobile.jpg',
        alt: 'Atelier créatif gratuit : un bonbon ou un sort ! Mercredi 28 octobre de 14h à 18h.',
        href: '',
        startsAt: '2026-09-15',
        endsAt: '2026-10-28T23:59:59',
        order: 0,
      },
      {
        imageDesktop: '/assets/img/hero/halloween-candy-bar-desktop.jpg',
        imageMobile: '/assets/img/hero/halloween-candy-bar-mobile.jpg',
        alt: 'Animation gratuite : Candy bar Halloween, samedi 31 octobre dès 14h.',
        href: '',
        startsAt: '2026-09-15',
        endsAt: '2026-10-31T23:59:59',
        order: 1,
      },
      {
        imageDesktop: '/assets/img/hero/black-friday-desktop.jpg',
        imageMobile: '/assets/img/hero/black-friday-mobile.jpg',
        alt: 'Grand Jeu Black Friday : faites tourner la Roue ! Vendredi 27 novembre de 14h à 18h, tentez de gagner le gros lot grâce à la Roue de la Chance.',
        href: '',
        startsAt: '2026-09-15',
        endsAt: '2026-11-27T23:59:59',
        order: 2,
      },
      {
        // visuel générique (toujours affiché) : garantit que le slider n'est jamais vide
        imageDesktop: '/assets/img/hero/generique-desktop.jpg',
        imageMobile: '/assets/img/hero/generique-mobile.jpg',
        alt: 'On se retrouve à Centre Sud ! Vos boutiques préférées : shopping, pause gourmande, animations pour les enfants ou simplement envie de flâner… À chacun sa raison de passer à Centre Sud !',
        href: '#boutiques',
        startsAt: '',
        endsAt: '',
        order: 3,
      },
    ],
  },

  boutiques: {
    icon: '/assets/pictos/cats/boutiques.svg',
    title: 'Boutiques & restaurants',
    searchLabel: 'Recherche',
    searchPlaceholder: 'Nom de la boutique…',
    categories: [
      { id: 'tous', label: 'Tous', icon: '/assets/pictos/cats/tous.svg' },
      { id: 'beaute', label: 'Beauté & santé', icon: '/assets/pictos/cats/beaute.svg' },
      { id: 'alimentation', label: 'Alimentation', icon: '/assets/pictos/cats/alimentation.svg' },
      { id: 'culture', label: 'Culture & loisirs', icon: '/assets/pictos/cats/culture.svg' },
      { id: 'maison', label: 'Maison & déco', icon: '/assets/pictos/cats/maison.svg' },
      { id: 'services', label: 'Services', icon: '/assets/pictos/cats/services.svg' },
      { id: 'mode', label: 'Mode', icon: '/assets/pictos/cats/mode.svg' },
      { id: 'restaurants', label: 'Restaurants', icon: '/assets/pictos/cats/restaurants.svg' },
      { id: 'bijoux', label: 'Bijoux', icon: '/assets/pictos/cats/bijoux.svg' },
    ],
    shops: [
      { name: 'Action', logo: '/assets/logos/action.png', category: 'maison', plan: 16, highlight: '/assets/img/plan-hl/hl-16.png' },
      { name: 'Carrefour', logo: '/assets/logos/carrefour.svg', category: 'alimentation', plan: 58 },
      { name: 'Darjeeling', logo: '/assets/logos/darjeeling.png', category: 'mode', plan: 43, highlight: '/assets/img/plan-hl/hl-43.png' },
      { name: 'de Neuville', logo: '/assets/logos/de-neuville.svg', category: 'alimentation', plan: 23, highlight: '/assets/img/plan-hl/hl-23.png' },
      { name: "Générale d'Optique", logo: '/assets/logos/generale-optique.svg', category: 'beaute', plan: 39, highlight: '/assets/img/plan-hl/hl-39.png' },
      { name: "Histoire d'Or", logo: '/assets/logos/histoire-dor.jpg', category: 'bijoux', plan: 42, highlight: '/assets/img/plan-hl/hl-42.png' },
      { name: 'Home Industrie', logo: '/assets/logos/home-industrie.svg', category: 'maison', plan: 48, highlight: '/assets/img/plan-hl/hl-48.png' },
      { name: 'Komel Cosmétique', logo: '', category: 'beaute', plan: 26, highlight: '/assets/img/plan-hl/hl-26.png' },
      { name: 'La Boutique des Artisans', logo: '/assets/logos/boutique-artisans.png', category: 'culture', plan: 28, highlight: '/assets/img/plan-hl/hl-28.png' },
      { name: 'Mastour Collection', logo: '/assets/logos/mastour.jpg', category: 'mode', plan: 7, highlight: '/assets/img/plan-hl/hl-7.png' },
      { name: 'New Yorker', logo: '/assets/logos/new-yorker.svg', category: 'mode', plan: 2, highlight: '/assets/img/plan-hl/hl-2.png' },
      { name: 'Nocibé', logo: '/assets/logos/nocibe.svg', category: 'beaute', plan: 14, highlight: '/assets/img/plan-hl/hl-14.png' },
      { name: 'Normal', logo: '/assets/logos/normal.svg', category: 'beaute', plan: 32, highlight: '/assets/img/plan-hl/hl-32.png' },
      { name: 'Pascal Coste', logo: '/assets/logos/pascal-coste.jpg', category: 'services', plan: 53, highlight: '/assets/img/plan-hl/hl-53.png' },
      { name: 'SFR', logo: '/assets/logos/sfr.svg', category: 'services', plan: 27, highlight: '/assets/img/plan-hl/hl-27.png' },
      { name: 'Snipes', logo: '/assets/logos/snipes.svg', category: 'mode', plan: 34, highlight: '/assets/img/plan-hl/hl-34.png' },
      { name: 'Toscane', logo: '/assets/logos/toscane.svg', category: 'mode', plan: 51, highlight: '/assets/img/plan-hl/hl-51.png' },
    ],
  },

  bonsPlans: {
    icon: '/assets/pictos/sections/bons-plans.svg',
    title: 'Bons plans',
    moreLabel: 'En voir plus',
    // Carte : `offerTitle` (gras, facultatif) puis `offer` (normal, facultatif).
    // Survol : `hoverTitle` (capitales), `hoverText` (paragraphes séparés par une
    // ligne vide, retour à la ligne simple conservé), `hoverNote` (mentions « * »).
    // startsAt/endsAt vides = offre permanente (affichée en dernier).
    items: [
      {
        shop: 'Nocibé',
        logo: '/assets/img/bons-plans/logo-nocibe.jpg',
        image: '/assets/img/bons-plans/nocibe-fidelite.jpg',
        offerTitle: 'Programme de fidélité gratuit.',
        offer: 'Accédez aux offres réservées aux membres.',
        hoverTitle: 'Votre fidélité récompensée !',
        hoverText: '1€ dépensé = 1 point fidélité\n\nTous les 200 points choisissez votre récompense : un bon d’achat de 10€ ou un cadeau*',
        hoverNote: '*Cadeau à choisir en boutique, selon les stocks disponibles. Conditions sur nocibe.fr.',
        startsAt: '',
        endsAt: '',
        order: 0,
      },
      {
        shop: 'De Neuville',
        logo: '/assets/img/bons-plans/logo-deneuville.jpg',
        image: '/assets/img/bons-plans/deneuville-privilege.jpg',
        offerTitle: 'Programme Privilège',
        offer: 'Profitez de remises et avantages exclusifs',
        hoverTitle: 'Découvrez le programme privilège',
        hoverText: '1€ dépensé = 1 point fidélité\n\nDès 100 points, recevez 5 € en bon d’achat*\nLe mois de votre anniversaire : vos points sont doublés à chaque achat et recevez un cadeau gourmand à venir chercher en boutique.',
        hoverNote: '*Conditions du Programme Privilège sur chocolat-deneuville.com.',
        startsAt: '',
        endsAt: '',
        order: 1,
      },
      {
        shop: 'De Neuville',
        logo: '/assets/img/bons-plans/logo-deneuville.jpg',
        image: '/assets/img/bons-plans/deneuville-ballotins.jpg',
        offerTitle: '',
        offer: 'Offre sur les ballotins',
        hoverTitle: 'Les prix fondent, le plaisir reste !',
        hoverText: 'Grâce à la baisse du prix du cacao, nous faisons fondre nos prix…\n\nBallotin de 220g dès 16,90€ (7,68€/100g)\nau lieu de 19,50€\nRendez-vous en boutique !',
        hoverNote: '',
        startsAt: '',
        endsAt: '',
        order: 2,
      },
      {
        shop: 'Générale d’Optique',
        logo: '/assets/img/bons-plans/logo-generale-optique.jpg',
        image: '/assets/img/bons-plans/generale-optique.jpg',
        offerTitle: '',
        offer: 'Offre optique',
        hoverTitle: 'Jusqu’à 200€ de remise sur vos lunettes de vue*',
        hoverText: '',
        hoverNote: '*Offre valable du 01/01/2026 au 31/12/2026 dans le magasin Générale d’Optique de Centre Sud Le Mans. Bénéficiez de 100€ de remise pour l’achat d’un équipement composé d’une monture + 2 verres correcteurs unifocaux, d’une valeur minimum de 200€ ; ou de 200€ de remise pour l’achat d’un équipement d’une valeur minimum de 400€, composé d’une monture + 2 verres correcteurs progressifs. Cette offre donne droit à la 2e et 3e paire ; la remise ne s’applique pas à la 2e et 3e paire. Hors montures comprenant des composants électroniques et/ou de l’Intelligence Artificielle dont les Meta ou Nuance Audio…. Remise effectuée en caisse. Non cumulable. Dispositifs médicaux CE. Janvier 2026.',
        startsAt: '2026-01-01',
        endsAt: '2026-12-31T23:59:59',
        order: 3,
      },
      {
        shop: 'Générale d’Optique',
        logo: '/assets/img/bons-plans/logo-generale-optique.jpg',
        image: '/assets/img/bons-plans/generale-solaire.jpg',
        offerTitle: '',
        offer: 'Offre solaire',
        hoverTitle: '20% de remise sur vos lunettes de soleil*',
        hoverText: '',
        // TODO client : « Bénéficiez de Bénéficiez de » est doublé dans le texte fourni
        hoverNote: '*Offre valable du 01/01/2026 au 31/12/2026 dans le magasin Générale d’Optique de Centre Sud Le Mans. Bénéficiez de Bénéficiez de 20% de remise sur une sélection de lunettes de soleil non correctrices, à partir de 100€, identifiées en magasin. Hors montures comprenant des composants électroniques et/ou de l’Intelligence Artificielle dont les Meta ou Nuance Audio…. Remise effectuée en caisse. Non cumulable. Janvier 2026.',
        startsAt: '2026-01-01',
        endsAt: '2026-12-31T23:59:59',
        order: 4,
      },
      {
        shop: 'Snipes',
        logo: '/assets/img/bons-plans/logo-snipes.jpg',
        image: '/assets/img/bons-plans/snipes-club.jpg',
        offerTitle: 'Programme de fidélité gratuit.',
        offer: '',
        hoverTitle: 'Plus d’avantages pour les membres',
        hoverText: 'Profite de ton avantage de bienvenue de -10%, collecte des COINS pour obtenir des réductions et bénéficie d’offres exclusives réservées aux membres ainsi que d’un accès anticipé aux promotions.\n\nInscris-toi gratuitement !',
        hoverNote: '',
        startsAt: '',
        endsAt: '',
        order: 5,
      },
    ],
  },

  actualites: {
    icon: '/assets/pictos/sections/actualites.svg',
    title: 'Actualités',
    // hoverTitle/hoverText : contenu du panneau dégradé affiché au survol (cf. maquette OVER) ;
    // hoverText = paragraphes séparés par des lignes vides
    // startsAt/endsAt vides = actualité permanente (affichée en dernier)
    // Carte : `date` (jour), `title`, `text` (retours à la ligne conservés).
    // Survol : `hoverDate` (jour + horaires), `hoverTitle`, `hoverText` : une ligne
    // vide sépare deux paragraphes, DEUX lignes vides créent un espace plus grand
    // (avant le bloc Date / Horaires / Lieu). Textes du graphiste (PSD actualités).
    items: [
      {
        date: 'Mercredi 28 octobre',
        startsAt: '2026-09-15',
        endsAt: '2026-10-28T23:59:59',
        order: 0,
        title: 'Un bonbon\nou un sort !',
        text: 'Viens customiser ton sac à bonbons d’halloween.\nCentre Sud invite les enfants à un atelier créatif gratuit pour préparer Halloween.',
        image: '/assets/img/actualites/halloween-atelier.jpg',
        href: '#',
        hoverDate: 'Mercredi 28 octobre de 14h à 18h',
        hoverTitle: 'Un bonbon ou un sort : crée ton sac pour Halloween !',
        hoverSubtitle: '',
        hoverText:
          'Centre Sud invite les enfants à un atelier créatif gratuit pour préparer Halloween.\n\n' +
          'Avant de partir à la chasse aux bonbons, place à l’imagination ! Les enfants pourront personnaliser leur sac à bonbons à l’aide de feutres textiles. Citrouilles rigolotes, petits fantômes ou chauves-souris : à chacun de dessiner au gré de ses envies. Un moment créatif et ludique pour repartir avec un sac unique, prêt à accueillir les gourmandises d’Halloween !\n\n\n' +
          'Date : mercredi 28 octobre\nHoraires : de 14h à 18h\nLieu : centre commercial Centre Sud Le Mans\n\n' +
          'Atelier gratuit',
      },
      {
        date: 'Samedi 31 octobre',
        startsAt: '2026-09-15',
        endsAt: '2026-10-31T23:59:59',
        order: 1,
        title: 'Un Candy Bar pour les petits courageux !',
        text: 'Centre Sud vous donne rendez-vous pour un Halloween gourmand ! Barbe à papa et bonbons attendent les petits courageux.',
        image: '/assets/img/actualites/halloween-candy-bar.jpg',
        href: '#',
        hoverDate: 'Samedi 31 octobre de 14h à 18h30',
        hoverTitle: 'Un Candy Bar pour les petits courageux !',
        hoverSubtitle: '',
        hoverText:
          'Centre Sud vous donne rendez-vous pour un Halloween gourmand !\n\n' +
          'Barbe à papa et bonbons attendent les petits courageux devant notre effrayant Candy Bar. Entre deux boutiques, venez partager une pause sucrée en famille et savourer l’ambiance d’Halloween.\n\n' +
          'Petits fantômes, sorcières et monstres gourmands : rendez-vous dès 14h pour faire le plein de douceurs !\n\n\n' +
          'Date : samedi 31 octobre\nHoraires : de 14h à 18h30\nLieu : centre commercial Centre Sud Le Mans\n\n' +
          'Animation gratuite',
      },
      {
        date: 'Vendredi 27 novembre',
        startsAt: '2026-09-15',
        endsAt: '2026-11-27T23:59:59',
        order: 2,
        title: 'Faites tourner la Roue de la Chance !',
        text: 'Et si la chance vous souriait ?\nFaites tourner la roue et tentez de remporter le gros lot !',
        image: '/assets/img/actualites/black-friday.jpg',
        href: '#',
        hoverDate: 'Vendredi 27 novembre de 14h à 18h',
        hoverTitle: 'Faites tourner la Roue de la Chance !',
        hoverSubtitle: '',
        hoverText:
          'Venez tenter votre chance à Centre Sud !\n\n' +
          'Et si la chance vous souriait ?\nFaites tourner la roue et tentez de remporter le gros lot ! À l’occasion de votre visite, profitez d’une animation gratuite pour partager un moment ludique et plein de suspense.\n\n' +
          'Une hôtesse sera présente pour vous accueillir, vous expliquer les règles et vous accompagner.\nIl ne restera plus qu’à faire tourner la roue… et à croiser les doigts !\nAlors, la roue vous portera-t-elle bonheur ?\n\n\n' +
          'Date : vendredi 27 novembre\nHoraires : de 14h à 18h\nLieu : centre commercial Centre Sud Le Mans\n\n' +
          'Animation gratuite',
      },
    ],
    strip: [
      '/assets/img/strip-0.jpg',
      '/assets/img/strip-1.jpg',
      '/assets/img/strip-2.jpg',
      '/assets/img/strip-3.jpg',
      '/assets/img/strip-4.jpg',
      '/assets/img/strip-5.jpg',
    ],
  },

  services: {
    icon: '/assets/pictos/services/services.svg',
    title: 'Services',
    items: [
      {
        icon: '/assets/pictos/services/aire-jeux.svg',
        title: 'Aire de jeux extérieure',
        text: "Le centre commercial dispose d'une aire de jeux extérieure dédiée aux enfants.",
      },
      {
        icon: '/assets/pictos/services/parking.svg',
        title: 'Parking gratuit de 1 900 places',
        text: "Profitez d'un parking gratuit pour effectuer vos achats en toute sérénité.",
      },
      {
        icon: '/assets/pictos/services/recharge.svg',
        title: 'Recharge PC/mobile',
        text: 'Rechargez vos appareils électroniques grâce aux prises électriques disponibles sur les espaces repos.',
      },
      {
        icon: '/assets/pictos/services/toilets.svg',
        title: 'Toilettes & nurserie',
        text: 'Des toilettes homme / femme et un espace bébé entièrement équipé sont à votre disposition pour votre confort.',
      },
      {
        icon: '/assets/pictos/services/espace-repos.svg',
        title: 'Espace repos',
        text: 'Faites une pause dans nos espaces de repos spécialement aménagés pour vous détendre et reprendre des forces.',
      },
      {
        icon: '/assets/pictos/services/food-court.svg',
        title: 'Food court',
        text: 'Faites une pause gourmande dans notre Food Court et découvrez une sélection variée de restaurants et de spécialités.',
      },
      {
        icon: '/assets/pictos/services/consigne-casque.svg',
        title: 'Consigne casque',
        text: 'Une consigne casque est mise à disposition au sein du centre, spécialement pensée pour les utilisateurs de deux-roues.',
      },
      {
        icon: '/assets/pictos/services/amazon.svg',
        title: 'Amazon locker',
        text: 'Amazon Locker vous fournit un point de livraison en libre-service pour récupérer et retourner vos colis Amazon.fr.',
      },
      {
        icon: '/assets/pictos/services/wifi.svg',
        title: 'Wifi',
        text: "Restez connecté grâce au Wi-Fi gratuit disponible dans l'ensemble du centre commercial.",
      },
      {
        icon: '/assets/pictos/services/service-minute.svg',
        title: 'Service minute',
        text: 'Profitez de notre service minute : reproduction de clés, cordonnerie, remplacement de piles de montres.',
      },
    ],
  },

  acces: {
    icon: '/assets/pictos/access/acces.svg',
    title: 'Accès',
    map: '/assets/img/carte-acces.svg',
    addressTitle: 'Centre Sud Le Mans',
    address: '257 avenue Georges Durand\n72100 Le Mans',
    mapsLabel: 'Y aller avec Google Maps',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Centre+Sud+257+avenue+Georges+Durand+72100+Le+Mans',
    // Lignes d'accès : `tag` = libellé coloré devant le titre (tone : 'bleu' | 'vert',
    // teinte aussi le picto), **texte** = gras. title est facultatif.
    tabs: [
      {
        id: 'transports',
        label: 'En transports en commun',
        icons: ['/assets/pictos/access/tram.svg', '/assets/pictos/access/bus.svg'],
        heading: 'Accès immédiat à partir des transports en commun',
        lines: [
          {
            icon: '/assets/pictos/access/tram.svg',
            tag: 'Tram',
            tone: 'bleu',
            title: '**Ligne T1**, station « Glonnières - Centre Sud »',
            details: [
              '**Depuis le Centre-ville** prendre direction T1 Antarès-MMArena',
              '**Depuis le Sud** prendre direction T1 Le Mans Université',
            ],
          },
          {
            icon: '/assets/pictos/access/bus.svg',
            tag: 'Bus',
            tone: 'vert',
            title: '**Ligne 10**, station « Glonnières - Centre Sud »',
            details: [],
          },
        ],
      },
      {
        id: 'velo',
        label: 'En vélo',
        icons: ['/assets/pictos/access/velo.svg'],
        heading: 'Utilisez les bandes et pistes cyclables du Mans',
        lines: [
          {
            icon: '/assets/pictos/access/velo.svg',
            tag: '',
            tone: 'vert',
            title: '',
            details: [
              'Environ **200 km de bandes et pistes** permettent de parcourir la Métropole du Mans et joindre Centre Sud.',
            ],
          },
        ],
      },
      {
        id: 'voiture',
        label: 'En voiture',
        icons: ['/assets/pictos/access/voiture.svg'],
        heading: 'Parking gratuit de 1 900 places',
        lines: [
          {
            icon: '/assets/pictos/access/voiture.svg',
            tag: '',
            tone: 'bleu',
            title: '',
            details: [
              '**Depuis le Centre-ville du Mans**, prendre la « route de Tours », D338, avenue Georges Durand.',
              '**Depuis la « Voie de la Liberté »**, suivre « Pôle Santé Sud » et « Le Mans ».',
              '**Depuis le Sud et Tours**, entrer dans Le Mans par « La route de Tours » et longer la ligne de tram jusqu’à Centre Sud.',
            ],
          },
        ],
      },
    ],
  },

  planHoraires: {
    icon: '/assets/pictos/sections/plan-horaires.svg',
    title: 'Plan et horaires',
    plan: '/assets/img/plan-centre.png',
    // contours des cellules (survol du plan) : { "16": "M…Z", … } en repère 1536×1024
    shapes: '/assets/img/plan-shapes.json',
    shopsTitle: 'Boutiques du plan',
    hoursTitle: 'Horaires Centre Sud',
    hours: [
      { day: 'Lundi', time: '10:00 - 21:30' },
      { day: 'Mardi', time: '10:00 - 21:30' },
      { day: 'Mercredi', time: '10:00 - 21:30' },
      { day: 'Jeudi', time: '10:00 - 21:30' },
      { day: 'Vendredi', time: '10:00 - 21:30' },
      { day: 'Samedi', time: '10:00 - 21:00' },
      { day: 'Dimanche', time: 'Fermé' },
    ],
    restaurantsTitle: 'Restaurants',
    restaurantsNote: 'Ouverts 7J/7',
    // Repère Carrefour posé sur le plan (position en % de l'image)
    carrefour: { logo: '/assets/logos/carrefour.svg', plan: 58, x: 81.9, y: 82.3 },
    // Numéros de cellules dessinés sur le plan (position en % de l'image, relevée sur la maquette)
    badges: [
      { n: 1, x: 8.05, y: 90.46 }, { n: 2, x: 12.63, y: 66.58 }, { n: 3, x: 16.96, y: 42.86 },
      { n: 6, x: 12.09, y: 42.1 }, { n: 7, x: 18.83, y: 36.21 },
      // n° 8 retiré : sa cellule existe sur la maquette mais pas sur le plan_nu.psd livré (à confirmer avec le graphiste)
      { n: 9, x: 17.67, y: 16.89 }, { n: 10, x: 26.89, y: 9.79 }, { n: 11, x: 30.35, y: 9.34 },
      { n: 12, x: 34.01, y: 9.13 }, { n: 13, x: 36.71, y: 11.94 }, { n: 14, x: 42.63, y: 9.36 },
      { n: 15, x: 51.8, y: 8.63 }, { n: 16, x: 68.24, y: 13.37 }, { n: 17, x: 72.25, y: 37.37 },
      { n: 18, x: 73.65, y: 44.48 }, { n: 19, x: 75.22, y: 52.29 }, { n: 20, x: 70.84, y: 56.78 },
      { n: 21, x: 61.1, y: 55.06 }, { n: 22, x: 55.35, y: 48.17 }, { n: 23, x: 51.28, y: 47.02 },
      { n: 24, x: 53.7, y: 53.9 }, { n: 25, x: 53.56, y: 57.6 }, { n: 26, x: 54.06, y: 61.51 },
      { n: 27, x: 53.84, y: 68.57 }, { n: 28, x: 54.46, y: 73.2 }, { n: 29, x: 53.92, y: 77.54 },
      { n: 30, x: 53.87, y: 83.14 }, { n: 31, x: 40.19, y: 85.33 }, { n: 32, x: 41.22, y: 70.11 },
      { n: 33, x: 40.25, y: 51.34 }, { n: 34, x: 29.95, y: 48.52 }, { n: 35, x: 30.03, y: 57.79 },
      { n: 36, x: 30.41, y: 62.08 }, { n: 37, x: 27.78, y: 66.28 }, { n: 38, x: 32.17, y: 69.47 },
      { n: 39, x: 30.76, y: 78.16 }, { n: 40, x: 30.3, y: 85.63 }, { n: 41, x: 30.2, y: 33.41 },
      { n: 42, x: 28.31, y: 27.39 }, { n: 43, x: 32.67, y: 26.99 }, { n: 44, x: 37.49, y: 26.84 },
      { n: 45, x: 42.54, y: 26.37 }, { n: 46, x: 47.92, y: 26.17 }, { n: 47, x: 52.81, y: 25.92 },
      { n: 48, x: 59.0, y: 27.95 }, { n: 49, x: 60.79, y: 33.08 }, { n: 50, x: 59.13, y: 37.78 },
      { n: 51, x: 55.15, y: 34.71 }, { n: 52, x: 50.56, y: 33.62 }, { n: 53, x: 46.96, y: 33.91 },
      { n: 54, x: 42.29, y: 33.26 }, { n: 55, x: 37.17, y: 33.26 }, { n: 56, x: 93.98, y: 38.71 },
      { n: 57, x: 90.5, y: 50.86 },
    ],
  },

  footer: {
    newsletterLabel: 'Inscription à la newsletter',
    newsletterPlaceholder: 'Votre email',
    newsletterSuccess: 'Merci, votre inscription est bien enregistrée.',
    newsletterError: 'Inscription impossible pour le moment, merci de réessayer.',
    copyright: 'Copyright © 2026 Centre Sud',
    legalLinks: [
      { label: 'Mentions légales', href: '/legal' },
      { label: 'Politique de confidentialité', href: '/legal#confidentialite' },
      { label: 'Données personnelles', href: '/legal#donnees' },
      { label: 'Politique de cookies', href: '/legal#cookies' },
    ],
  },

  legal: {
    title: 'Mentions légales',
    backLabel: 'Retour au site',
    sections: [
      { title: 'Éditeur', body: 'Centre Sud Le Mans - 257 avenue Georges Durand, 72100 Le Mans. (Contenu à compléter.)' },
      { title: 'Politique de confidentialité', body: 'Contenu à compléter.' },
      { title: 'Données personnelles', body: 'Contenu à compléter.' },
      { title: 'Politique de cookies', body: 'Contenu à compléter.' },
    ],
  },
}

// ── Transformation Strapi ────────────────────────────────────────────────────
// Résout un champ média Strapi en URL absolue, sinon renvoie le fallback local.
function mediaUrl(m, fallback) {
  const url = m && (m.url || m.data?.attributes?.url)
  if (!url) return fallback ?? ''
  return /^https?:\/\//i.test(url) ? url : `${STRAPI_URL}${url}`
}
// Première valeur non vide.
function pick(v, fb) {
  return v === undefined || v === null || v === '' ? fb : v
}
// Mappe un tableau Strapi (ou retombe sur le fallback local s'il est vide).
function arr(list, fb, map) {
  return Array.isArray(list) && list.length ? list.map(map) : fb
}

function transform(page) {
  const fb = CONTENT
  const p = page || {}
  return {
    seo: {
      lang: pick(p.seo?.lang, fb.seo.lang),
      title: pick(p.seo?.title, fb.seo.title),
      description: pick(p.seo?.description, fb.seo.description),
      keywords: pick(p.seo?.keywords, fb.seo.keywords),
      siteUrl: pick(p.seo?.siteUrl, fb.seo.siteUrl),
      ogImage: mediaUrl(p.seo?.ogImage, fb.seo.ogImage),
    },
    site: {
      logo: mediaUrl(p.site?.logo, fb.site.logo),
      openLabel: pick(p.site?.openLabel, fb.site.openLabel),
    },
    nav: {
      items: arr(p.nav?.items, fb.nav.items, (n) => ({ label: n.label, anchor: n.anchor })),
    },
    hero: {
      slides: arr(p.hero?.slides, fb.hero.slides, (s, i) => ({
        imageDesktop: mediaUrl(s.imageDesktop, fb.hero.slides[i]?.imageDesktop),
        imageMobile: mediaUrl(s.imageMobile, fb.hero.slides[i]?.imageMobile),
        alt: s.alt || '',
        href: s.href || '',
        startsAt: s.startsAt || '',
        endsAt: s.endsAt || '',
        order: s.order ?? i,
      })),
    },
    boutiques: {
      icon: mediaUrl(p.boutiques?.icon, fb.boutiques.icon),
      title: pick(p.boutiques?.title, fb.boutiques.title),
      searchLabel: pick(p.boutiques?.searchLabel, fb.boutiques.searchLabel),
      searchPlaceholder: pick(p.boutiques?.searchPlaceholder, fb.boutiques.searchPlaceholder),
      categories: arr(p.boutiques?.categories, fb.boutiques.categories, (c, i) => ({
        id: c.slug,
        label: c.label,
        icon: mediaUrl(c.icon, fb.boutiques.categories[i]?.icon),
      })),
      shops: arr(p.boutiques?.shops, fb.boutiques.shops, (s) => ({
        name: s.name,
        logo: mediaUrl(s.logo, ''),
        category: s.category?.slug || s.category || '',
        plan: Number(s.plan),
        highlight: mediaUrl(s.highlight, ''),
      })),
    },
    bonsPlans: {
      icon: mediaUrl(p.bonsPlans?.icon, fb.bonsPlans.icon),
      title: pick(p.bonsPlans?.title, fb.bonsPlans.title),
      moreLabel: pick(p.bonsPlans?.moreLabel, fb.bonsPlans.moreLabel),
      items: arr(p.bonsPlans?.items, fb.bonsPlans.items, (b, i) => ({
        shop: b.shop,
        offerTitle: b.offerTitle || '',
        offer: b.offer || '',
        startsAt: b.startsAt || '',
        endsAt: b.endsAt || '',
        order: b.order ?? i,
        image: mediaUrl(b.image, ''),
        logo: mediaUrl(b.logo, ''),
        hoverTitle: b.hoverTitle || '',
        hoverText: b.hoverText || '',
        hoverNote: b.hoverNote || '',
      })),
    },
    actualites: {
      icon: mediaUrl(p.actualites?.icon, fb.actualites.icon),
      title: pick(p.actualites?.title, fb.actualites.title),
      items: arr(p.actualites?.items, fb.actualites.items, (a, i) => ({
        date: a.date,
        startsAt: a.startsAt || '',
        endsAt: a.endsAt || '',
        order: a.order ?? i,
        title: a.title,
        text: a.text,
        image: mediaUrl(a.image, fb.actualites.items[i]?.image),
        href: a.href || '#',
        hoverDate: a.hoverDate || '',
        hoverTitle: a.hoverTitle || '',
        hoverSubtitle: a.hoverSubtitle || '',
        hoverText: a.hoverText || '',
      })),
      strip: arr(p.actualites?.strip, fb.actualites.strip, (m, i) =>
        mediaUrl(m, fb.actualites.strip[i])
      ),
    },
    services: {
      icon: mediaUrl(p.services?.icon, fb.services.icon),
      title: pick(p.services?.title, fb.services.title),
      items: arr(p.services?.items, fb.services.items, (s, i) => ({
        icon: mediaUrl(s.icon, fb.services.items[i]?.icon),
        title: s.title,
        text: s.text,
      })),
    },
    acces: {
      icon: mediaUrl(p.acces?.icon, fb.acces.icon),
      title: pick(p.acces?.title, fb.acces.title),
      map: mediaUrl(p.acces?.map, fb.acces.map),
      addressTitle: pick(p.acces?.addressTitle, fb.acces.addressTitle),
      address: pick(p.acces?.address, fb.acces.address),
      mapsLabel: pick(p.acces?.mapsLabel, fb.acces.mapsLabel),
      mapsUrl: pick(p.acces?.mapsUrl, fb.acces.mapsUrl),
      tabs: arr(p.acces?.tabs, fb.acces.tabs, (t, i) => ({
        id: t.slug,
        label: t.label,
        icons: arr(t.icons, fb.acces.tabs[i]?.icons || [], (ic) => mediaUrl(ic, '')),
        heading: t.heading,
        lines: (t.lines || []).map((l) => ({
          icon: mediaUrl(l.icon, ''),
          tag: l.tag || '',
          tone: l.tone || 'bleu',
          title: l.title || '',
          details: Array.isArray(l.details) ? l.details : [],
        })),
      })),
    },
    planHoraires: {
      icon: mediaUrl(p.planHoraires?.icon, fb.planHoraires.icon),
      title: pick(p.planHoraires?.title, fb.planHoraires.title),
      plan: mediaUrl(p.planHoraires?.plan, fb.planHoraires.plan),
      shapes: mediaUrl(p.planHoraires?.shapes, fb.planHoraires.shapes),
      shopsTitle: pick(p.planHoraires?.shopsTitle, fb.planHoraires.shopsTitle),
      hoursTitle: pick(p.planHoraires?.hoursTitle, fb.planHoraires.hoursTitle),
      hours: arr(p.planHoraires?.hours, fb.planHoraires.hours, (h) => ({
        day: h.day,
        time: h.time,
      })),
      restaurantsTitle: pick(p.planHoraires?.restaurantsTitle, fb.planHoraires.restaurantsTitle),
      restaurantsNote: pick(p.planHoraires?.restaurantsNote, fb.planHoraires.restaurantsNote),
      carrefour: p.planHoraires?.carrefour
        ? {
          logo: mediaUrl(p.planHoraires.carrefour.logo, fb.planHoraires.carrefour.logo),
          plan: Number(pick(p.planHoraires.carrefour.plan, fb.planHoraires.carrefour.plan)),
          x: Number(pick(p.planHoraires.carrefour.x, fb.planHoraires.carrefour.x)),
          y: Number(pick(p.planHoraires.carrefour.y, fb.planHoraires.carrefour.y)),
        }
        : fb.planHoraires.carrefour,
      badges: pick(p.planHoraires?.badges, fb.planHoraires.badges),
    },
    footer: {
      newsletterLabel: pick(p.footer?.newsletterLabel, fb.footer.newsletterLabel),
      newsletterPlaceholder: pick(p.footer?.newsletterPlaceholder, fb.footer.newsletterPlaceholder),
      newsletterSuccess: pick(p.footer?.newsletterSuccess, fb.footer.newsletterSuccess),
      newsletterError: pick(p.footer?.newsletterError, fb.footer.newsletterError),
      copyright: pick(p.footer?.copyright, fb.footer.copyright),
      legalLinks: arr(p.footer?.legalLinks, fb.footer.legalLinks, (l) => ({
        label: l.label,
        href: l.href,
      })),
    },
    legal: {
      title: pick(p.legal?.title, fb.legal.title),
      backLabel: pick(p.legal?.backLabel, fb.legal.backLabel),
      sections: arr(p.legal?.sections, fb.legal.sections, (s) => ({
        title: s.title,
        body: s.body,
      })),
    },
  }
}

let cache = null

// Récupère le contenu.
// - STRAPI_URL vide : contenu local immédiat.
// - sinon : fetch + transform, avec quelques essais (Strapi Cloud se met en veille
//   et met ~30 s à se réveiller). Si le CMS reste injoignable, on retombe sur le
//   contenu local : le site s'affiche toujours.
const RETRY_DELAY = 1500
const REQUEST_TIMEOUT = 5000
const TOTAL_BUDGET = 12000

export async function fetchCcContent() {
  if (!STRAPI_URL) return CONTENT
  if (cache) return cache

  const deadline = Date.now() + TOTAL_BUDGET
  let attempt = 0
  while (Date.now() < deadline) {
    attempt++
    try {
      const res = await axios.get(`${STRAPI_URL}/api/page/home`, { timeout: REQUEST_TIMEOUT })
      cache = transform(res.data)
      return cache
    } catch (err) {
      console.warn(`[centre-sud] CMS injoignable (essai ${attempt})`, err?.message || err)
      if (Date.now() + RETRY_DELAY < deadline) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY))
      } else {
        break
      }
    }
  }

  // le contenu local est le miroir du contenu seedé : le site reste complet
  console.warn('[centre-sud] CMS injoignable — bascule sur le contenu local.')
  return CONTENT
}

/**
 * Programmation : ne garde que les éléments actuellement visibles, puis trie
 * « ce qui se termine le plus tôt d'abord », les éléments permanents (sans date
 * de fin) toujours en dernier ; à date de fin égale, on suit `order`.
 *
 * Le tri est fait ici et non en base : SQLite et PostgreSQL ne placent pas les
 * valeurs nulles au même endroit, le résultat différerait entre dev et prod.
 */
export function scheduled(items, now = new Date()) {
  const t = now.getTime()
  const stamp = (v) => {
    if (!v) return null
    const d = Date.parse(v)
    return Number.isNaN(d) ? null : d
  }
  return (items || [])
    .filter((it) => {
      const start = stamp(it.startsAt)
      const end = stamp(it.endsAt)
      if (start !== null && start > t) return false
      if (end !== null && end < t) return false
      return true
    })
    .sort((a, b) => {
      const ea = stamp(a.endsAt) ?? Infinity
      const eb = stamp(b.endsAt) ?? Infinity
      if (ea !== eb) return ea - eb
      return (a.order ?? 0) - (b.order ?? 0)
    })
}

export function getContent() {
  return CONTENT
}
